import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";

import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import { NotificationBell } from "../NotificationBell";
import { cn } from "../ui/cn";

/**
 * Sales call navigation.
 *
 * Deliberately the same shape as the CRM's and the Helpdesk's -- somebody
 * switching between three products twenty times a day should not have to
 * re-learn where things are.
 *
 * Short, because the module has one screen that matters. The middle block is
 * four slices of that one list, and the counts are the point: the pool and
 * the overdue pile are both work nobody has taken, and a queue you have to
 * click into to discover is empty is a queue nobody checks.
 *
 * Meetings are deliberately not here. A visit is worked by a different half of
 * the desk in a different place, and it has its own tile on the rail.
 */
export function SalesSidebar({ connected }: { connected: boolean }) {
  /*
   * One request behind every count here. The board already computes all of
   * them from a single read of the pool, so the sidebar and the page can never
   * disagree about how many calls are overdue.
   */
  const board = useQuery({
    queryKey: queryKeys.salesBoard,
    queryFn: salesApi.board,
    staleTime: 30_000,
  });

  return (
    <nav className="flex w-60 shrink-0 flex-col overflow-y-auto border-r border-line bg-app pb-3">
      <div className="flex items-center gap-2 px-4 py-4">
        <span className="text-sm font-semibold text-content">Sales calls</span>
        <span
          title={connected ? "Live updates connected" : "Reconnecting…"}
          className={cn(
            "size-2 shrink-0 rounded-full",
            connected ? "bg-sla-ok" : "bg-sla-warning",
          )}
        />
        <span className="ml-auto">
          <NotificationBell />
        </span>
      </div>

      {/*
        Above the queues, where the Helpdesk keeps its own -- somebody moving
        between the two products should find it in the same place.

        It used to sit at the bottom under "Finished", filed as a look back at
        work already done. That was the wrong reading of it: half the page is
        standing load and who is overdue, which is what you check *before*
        deciding which queue to open, not after.
      */}
      <ul className="mb-3 px-2">
        <SalesLink to="/sales/dashboard" label="Dashboard" />
      </ul>

      <ul className="px-2">
        <SalesLink to="/sales" label="Today" end />
        <SalesLink
          to="/sales/calls"
          label="All open calls"
          count={board.data?.kpi.open}
        />
        <SalesLink
          to="/sales/calls?mine=true"
          label="Assigned to me"
          count={board.data?.kpi.mine}
        />
        {/*
          The one count worth pulling the eye. Everything else here is a size;
          this one is a problem.
        */}
        <SalesLink
          to="/sales/calls?overdue=true"
          label="Needs attention"
          count={board.data?.kpi.overdue}
          highlight={(board.data?.kpi.overdue ?? 0) > 0}
        />
      </ul>

      <div className="mt-4 px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
        Finished
      </div>
      <ul className="px-2">
        {/* The brief asks for this by name. Closed calls stay searchable, and
            somebody looking for "what did we say to them in March" needs a door
            into them that is not a filter dropdown. */}
        <SalesLink to="/sales/closed" label="Closed calls" />
      </ul>
    </nav>
  );
}

/**
 * A nav link that knows about its query string.
 *
 * `NavLink`'s own `isActive` matches on the path alone, and four of the five
 * links here point at `/sales/calls` with different filters -- so the stock
 * behaviour lights all four up at once and the sidebar stops telling you where
 * you are. Comparing the search string as well is the whole difference.
 */
function SalesLink({
  to,
  label,
  count,
  highlight,
  end,
}: {
  to: string;
  label: string;
  count?: number;
  highlight?: boolean;
  end?: boolean;
}) {
  const location = useLocation();
  const [path, search = ""] = to.split("?");
  const active = end
    ? location.pathname === path
    : location.pathname === path &&
      normalise(location.search) === normalise(search);

  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
          active
            ? "bg-surface-2 font-medium text-content"
            : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined && count > 0 ? (
          <span
            className={cn(
              "shrink-0 tabular-nums",
              highlight
                ? "rounded-full bg-red-600 px-1.5 text-[10px] font-medium text-white"
                : "text-[11px] text-faint",
            )}
          >
            {count}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}

/** Sorted params, so `?a=1&b=2` and `?b=2&a=1` are the same view. */
function normalise(search: string): string {
  const params = new URLSearchParams(search);
  params.sort();
  return params.toString();
}
